import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, Camera, Share2, Copy, RefreshCw, ExternalLink } from 'lucide-react';
import apiClient from '../../services/apiClient';
import Button from '../ui/Button';
import { useStudentForm } from '../../hooks/useStudentForm';
import { validateStudentForm, showValidationErrorsWithToast } from '../../utils/formValidators';
import studentService from '../../services/studentService';
import { useToast } from '../../hooks/useToast';
import { PermissionGuard } from '../auth/PermissionGuard';
import { ButtonPermissions } from '../../types/permission';

/**
 * 编辑学生表单
 */
const StudentFormEdit = ({ initialData = {}, onBack, onSave, onTokenAccess, isSaving = false }) => {
  const {
    genderOptions,
    statusOptions,
  } = useStudentForm(initialData);
  const toast = useToast();
  // 表单字段引用
  const nameRef = useRef(null);
  const genderRef = useRef(null);
  const birthYearRef = useRef(null);
  const birthMonthRef = useRef(null);
  const gradeRef = useRef(null);
  const phoneRef = useRef(null);
  const referrerRef = useRef(null);
  const statusRef = useRef(null);
  const noteRef = useRef(null);
  // 用户账号字段引用
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  // 年级状态（只读显示）
  const [calculatedGrade, setCalculatedGrade] = useState(initialData.grade || '');
  
  // 访问令牌状态
  const [accessToken, setAccessToken] = useState(null);
  const [loadingToken, setLoadingToken] = useState(false);
  const [tokenError, setTokenError] = useState(false);

  // 微信绑定状态
  const [wechatBinding, setWechatBinding] = useState(null);
  const [loadingBinding, setLoadingBinding] = useState(false);

  // 防抖计时器引用
  const debounceTimerRef = useRef(null);

  // 初始化时计算年级并获取分享令牌和微信绑定状态
  useEffect(() => {
    if (initialData.birth_year) {
      handleBirthDateChange();
    }
    if (initialData.id) {
      fetchAccessToken();
      fetchWechatBinding();
    }
  }, [initialData.id]);

  // 获取微信绑定状态
  const fetchWechatBinding = async () => {
    if (!initialData.id) return;
    setLoadingBinding(true);
    try {
      const res = await studentService.getBindingStatus(initialData.id);
      // res.data 或是 res 视 axios 拦截器而定，这里兼容处理
      setWechatBinding(res.data || res);
    } catch (error) {
      console.error('[WechatBinding] 获取失败:', error);
    } finally {
      setLoadingBinding(false);
    }
  };

  // 解绑微信
  const handleUnbindWechat = async () => {
    if (!window.confirm('确定要解除该学生的微信绑定吗？学生需重新绑定后才能在小程序中登录。')) return;
    
    try {
      await studentService.unbindWechat(initialData.id);
      toast.success('微信解绑成功');
      fetchWechatBinding();
    } catch (error) {
      console.error('[WechatBinding] 解绑失败:', error);
      toast.error(error?.response?.data?.error || '解绑失败，请重试');
    }
  };

  // 获取访问令牌
  const fetchAccessToken = async () => {
    if (!initialData.id) return;
    setLoadingToken(true);
    setTokenError(false);
    try {
      console.log(`[AccessToken] 正在获取 ID 为 ${initialData.id} 的令牌...`);
      const res = await apiClient.get(`/api/admin/access-token/${initialData.id}`);
      console.log('[AccessToken] 获取结果:', res);
      
      const token = res.data?.token || res.data?.data?.token || (res.token ? res.token : null);
      if (token) {
        setAccessToken(token);
      }
    } catch (error) {
      console.error('[AccessToken] 获取失败:', error);
      setTokenError(true);
    } finally {
      setLoadingToken(false);
    }
  };

  // 兼容性复制链接函数
  const handleCopyLink = () => {
    if (!accessToken) return;
    
    // 优先使用外网域名，如果没有则回退到当前 host
    const domain = import.meta.env.PROD ? 'https://i.sumarts.cn' : window.location.origin;
    const accessUrl = `${domain}/token/${accessToken}`;
    
    // 方案 1: 现代 API (需要 HTTPS)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(accessUrl).then(() => {
        toast.success('访问链接已复制');
      }).catch(err => {
        console.error('Modern copy failed:', err);
        fallbackCopyTextToClipboard(accessUrl);
      });
    } else {
      // 方案 2: 传统方案 (支持 HTTP)
      fallbackCopyTextToClipboard(accessUrl);
    }
  };

  // 生成访问令牌
  const handleGenerateToken = async (e) => {
    if (e) e.preventDefault();
    if (!initialData.id) return;
    
    setLoadingToken(true);
    try {
      const res = await apiClient.post(`/api/admin/access-token/${initialData.id}/reset`);
      const token = res.data?.token || res.data?.data?.token || (res.token ? res.token : null);
      if (token) {
        setAccessToken(token);
        toast.success('快捷访问已开启');
      }
    } catch (error) {
      console.error('[AccessToken] 生成失败:', error);
      toast.error('开启失败，请重试');
    } finally {
      setLoadingToken(false);
    }
  };


  // 传统复制方案
  const fallbackCopyTextToClipboard = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      
      // 确保不可见但可操作
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('链接已复制 (兼容模式)');
      } else {
        throw new Error('execCommand copy failed');
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      alert('复制失败，请手动长按链接复制');
    }
  };

  // 自动计算年级 (防抖处理)
  const handleBirthDateChange = () => {
    // 清除之前的计时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const birthYear = birthYearRef.current?.value;
    const birthMonth = birthMonthRef.current?.value;
    
    if (!birthYear) {
      setCalculatedGrade('');
      return;
    }

    const currentYear = new Date().getFullYear();
    const age = currentYear - parseInt(birthYear);

    if (age >= 18) {
      setCalculatedGrade('');
      return;
    }

    // 设置 800ms 的防抖
    debounceTimerRef.current = setTimeout(async () => {
      try {
        console.log('正在计算年级:', { birthYear, birthMonth });
        const result = await studentService.calculateGrade({
          birth_year: parseInt(birthYear),
          birth_month: birthMonth ? parseInt(birthMonth) : undefined
        });
        setCalculatedGrade(result.grade);
      } catch (error) {
        console.error('计算年级失败:', error);
        // 不显示 toast，避免输入过程中的频繁干扰，只记录日志
      }
    }, 800);
  };

  // 处理保存
  const handleSave = () => {
    // 注意：防重入锁由父组件 App.jsx 的 isSavingRef 统一管理，此处不重复判断
    const formData = {
      id: initialData.id,
      name: nameRef.current?.value || '',
      gender: genderRef.current?.value || '',
      birth_year: birthYearRef.current?.value ? parseInt(birthYearRef.current.value) : null,
      birth_month: birthMonthRef.current?.value ? parseInt(birthMonthRef.current.value) : null,
      grade: calculatedGrade || '',
      contact: phoneRef.current?.value || '',
      hobbies: referrerRef.current?.value || '',  // 推荐人临时映射到 hobbies
      student_type: statusRef.current?.value || '在学',
      remarks: noteRef.current?.value || '',
      // 用户账号字段
      username: usernameRef.current?.value || '',
      password: passwordRef.current?.value || '',  // 只有填写时才修改密码
    };

    // 使用验证工具验证
    const validation = validateStudentForm(formData);
    if (!validation.valid) {
      showValidationErrorsWithToast(validation.errors, toast);
      return;
    }

    onSave(formData);
  };

  return (
    <div className="flex flex-col h-full bg-[#F7FAFC]">
      <header className="h-[56px] px-4 bg-white flex items-center justify-between border-b border-[#EDF2F7] flex-shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="p-1 -ml-1 text-[#2D3748]">
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-[#2D3748]">编辑学生</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 pb-24 space-y-4">
        {/* 头像上传 */}
        <div className="flex flex-col items-center justify-center py-6 bg-white rounded-xl border border-[#EDF2F7]">
          <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-2 border-2 border-dashed border-gray-300 ${initialData.avatar ? 'bg-blue-100' : 'bg-gray-100'}`}>
            {initialData.avatar ? (
              <span className="text-2xl font-bold text-[#3182CE]">{initialData.name?.[0]}</span>
            ) : (
              <Camera size={24} className="text-gray-400" />
            )}
          </div>
          <span className="text-xs text-[#3182CE] font-medium">修改头像</span>
        </div>

        {/* 基本信息表单 */}
        <div className="bg-white rounded-xl border border-[#EDF2F7] overflow-hidden">
          {/* 学生姓名 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              学生姓名
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={nameRef}
              type="text" 
              placeholder="请输入姓名" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
              defaultValue={initialData.name}
              required
            />
          </div>

          {/* 性别 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              性别
              <span className="text-red-500">*</span>
            </label>
            <select 
              ref={genderRef}
              className="flex-1 text-sm bg-transparent outline-none text-[#2D3748] dir-rtl text-right"
              defaultValue={initialData.gender}
              required
            >
              <option value="">请选择性别</option>
              {genderOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {/* 出生年份 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              出生年份
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={birthYearRef}
              type="number" 
              placeholder="如: 2018" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
              defaultValue={initialData.birth_year}
              min="1900"
              max={new Date().getFullYear()}
              onChange={handleBirthDateChange}
              required
            />
          </div>

          {/* 出生月份 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              出生月份
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={birthMonthRef}
              type="number" 
              placeholder="1-12" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
              defaultValue={initialData.birth_month}
              min="1"
              max="12"
              onChange={handleBirthDateChange}
              required
            />
          </div>

          {/* 年级（自动计算） */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568]">
              年级
            </label>
            <input 
              ref={gradeRef}
              type="text" 
              value={calculatedGrade}
              placeholder="根据出生日期自动计算" 
              className="flex-1 text-sm text-right bg-gray-100 outline-none text-[#2D3748] cursor-not-allowed"
              readOnly
            />
          </div>

          {/* 联系方式 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              联系方式
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={phoneRef}
              type="tel" 
              placeholder="请输入家长电话" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
              defaultValue={initialData.contact}
              required
            />
          </div>

          {/* 推荐人 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568]">
              推荐人
            </label>
            <input 
              ref={referrerRef}
              type="text" 
              placeholder="请输入推荐人姓名" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
              defaultValue={initialData.hobbies}
            />
          </div>

          {/* 状态 */}
          <div className="flex items-center px-4 py-3">
            <label className="w-20 text-sm font-medium text-[#4A5568]">
              状态
            </label>
            <select 
              ref={statusRef}
              className="flex-1 text-sm bg-transparent outline-none text-[#2D3748] dir-rtl text-right"
              defaultValue={initialData.student_type || "在学"}
            >
              {statusOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 备注 */}
        <div>
          <label className="text-sm font-medium text-[#4A5568] mb-2 block px-1">备注</label>
          <textarea 
            ref={noteRef}
            className="w-full bg-white border border-[#EDF2F7] rounded-xl p-3 text-sm h-24 focus:outline-none focus:border-[#3182CE]" 
            placeholder="请输入学生备注信息..."
            defaultValue={initialData.remarks}
          />
        </div>

        {/* 登录账号设置 */}
        <div className="bg-white rounded-xl border border-[#EDF2F7] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#EDF2F7] bg-gray-50">
            <span className="text-sm font-medium text-[#4A5568]">登录账号设置</span>
            {initialData.user_info?.has_password && (
              <span className="ml-2 text-xs text-green-600">（已设置密码）</span>
            )}
          </div>
          
          {/* 用户名 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568]">
              用户名
            </label>
            <input 
              ref={usernameRef}
              type="text" 
              placeholder="默认与学生姓名相同" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
              defaultValue={initialData.user_info?.username || initialData.name || ''}
            />
          </div>

          {/* 登录密码 */}
          <div className="flex items-center px-4 py-3">
            <label className="w-20 text-sm font-medium text-[#4A5568]">
              密码
            </label>
            <input 
              ref={passwordRef}
              type="text" 
              autoComplete="off"
              placeholder={
                initialData.user_info
                  ? (initialData.user_info.password ? '可直接修改密码' : '历史加密密码，留空不修改')
                  : '填写后开通登录账号（至少6位）'
              }
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
              defaultValue={initialData.user_info?.password || ''}
            />
          </div>
        </div>

        {/* 微信小程序绑定 */}
        <div className="bg-white rounded-xl border border-[#EDF2F7] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#EDF2F7] bg-gray-50 flex justify-between items-center">
            <span className="text-sm font-medium text-[#4A5568]">微信绑定状态</span>
            {loadingBinding ? (
              <span className="text-xs text-gray-400">加载中...</span>
            ) : wechatBinding?.bound ? (
              <span className="text-xs text-green-500 font-medium">已绑定</span>
            ) : (
              <span className="text-xs text-gray-400">未绑定</span>
            )}
          </div>
          
          <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {wechatBinding?.bound && wechatBinding?.avatar ? (
                <img src={wechatBinding.avatar} alt="avatar" className="w-10 h-10 rounded-full bg-gray-100" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                </div>
              )}
              
              <div>
                <div className="text-sm font-medium text-gray-800">
                  {wechatBinding?.bound ? (wechatBinding?.nickname || '微信用户') : '该学生暂未绑定微信'}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {wechatBinding?.bound 
                    ? '已绑定小程序，可直接免密登录' 
                    : '学生可使用系统账号密码在小程序中自助绑定'}
                </div>
              </div>
            </div>

            {wechatBinding?.bound && (
              <button
                type="button"
                onClick={handleUnbindWechat}
                className="px-4 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors whitespace-nowrap"
              >
                解除绑定
              </button>
            )}
          </div>
        </div>

        {/* 快捷访问 */}
        <div className="bg-white rounded-xl border border-[#EDF2F7] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#EDF2F7] bg-blue-50/30 flex justify-between items-center">
            <span className="text-sm font-medium text-[#3182CE] flex items-center gap-1.5">
              <ExternalLink size={16} />
              快捷访问 (免密直连)
            </span>
            {accessToken && (
              <button 
                type="button"
                onClick={handleGenerateToken}
                className="text-[10px] text-gray-400 flex items-center gap-0.5 hover:text-[#3182CE] transition-colors"
                title="重置令牌"
              >
                <RefreshCw size={10} />
                重置
              </button>
            )}
          </div>
          <div className="p-4">
            {loadingToken ? (
              <div className="py-4 flex justify-center">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : accessToken ? (
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg border border-dashed border-gray-200 break-all text-[11px] text-gray-500 font-mono">
                  {import.meta.env.PROD ? 'https://i.sumarts.cn' : window.location.origin}/token/{accessToken}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); handleCopyLink(); }}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#3182CE] text-white rounded-lg text-sm font-bold active:scale-[0.98] transition-all"
                  >
                    <Copy size={16} />
                    一键复制链接
                  </button>
                  <a
                    href={`/token/${accessToken}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-12 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg active:scale-[0.98]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink size={18} />
                  </a>
                </div>
                <p className="text-[10px] text-center text-gray-400">复制后可直接通过微信发送给家长，家长点开即可查看。</p>
              </div>
            ) : (
              <div className="text-center py-2">
                <p className="text-xs text-gray-500 mb-4">该学生尚未启用快捷访问。启用后，家长可免登录直接查看教学动态。</p>
                <button
                  type="button"
                  onClick={handleGenerateToken}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-white border-2 border-[#3182CE] text-[#3182CE] rounded-xl font-bold hover:bg-blue-50 transition-all active:scale-[0.98]"
                >
                  <ExternalLink size={18} />
                  开启快捷访问
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* 底部按钮区 - 编辑模式 */}
      <div className="h-[80px] bg-white border-t border-[#EDF2F7] px-6 flex items-center justify-between flex-shrink-0 z-10">
        <Button variant="secondary" onClick={onBack} className="w-32" disabled={isSaving}>取消</Button>
        <PermissionGuard permission={ButtonPermissions.students.edit}>
          <Button variant="primary" onClick={handleSave} className="w-32" disabled={isSaving}>
            {isSaving ? '保存中...' : '保存'}
          </Button>
        </PermissionGuard>
      </div>
    </div>
  );
};

export default StudentFormEdit;
